import {useCopilotUiReadiness} from '@github-ui/copilot-browser-latency/copilot-ui-readiness-context'
import {userErrorMetrics} from '@github-ui/copilot-browser-latency/user-visible-errors'
import {useCallback} from 'react'

// eslint-disable-next-line no-restricted-imports -- Permission slash commands are applied silently, so a toast is the only feedback surface.
import {useToastContext} from '@github-ui/toast/ToastContext'
import {isSessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import {APPROVAL_MODE_PRESENTATION} from '@github-ui/copilot-shared-ui/utils/approval-mode-presentation'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {parsePermissionCommand} from '@github-ui/one-copilot-composer/existing'

import {composerToastMessage} from '../components/session-logs/ahp/ComposerToastMessage'

import {useEstablishedApprovalModeMutation} from './use-established-approval-mode-mutation'

const CHANGE_ERROR = 'Could not change tool permissions. Try again.'
const LOCKED_ERROR = 'Tool permissions cannot be changed for this session.'
const UNKNOWN_MODE = 'Tool permissions are not reported for this session.'

/**
 * Applies a tool-permission slash command, reporting the outcome as a toast.
 *
 * Returns whether the text was a recognized command. A `true` result means the
 * composer must not dispatch it as a chat turn: these commands configure the
 * session over the relay and are not something the model should ever read.
 * Unrecognized arguments return `false` so the host still sees the raw text.
 */
export function usePermissionCommandHandler(): (text: string) => Promise<boolean> {
  const {addToast} = useToastContext()
  const {onApprovalModeChange} = useEstablishedApprovalModeMutation()
  const {permissionMode} = useOptionalEstablishedAhpSessionViewState()
  const surface = useCopilotUiReadiness()?.open.surface

  return useCallback(
    async (text: string) => {
      const command = parsePermissionCommand(text)
      if (!command || command.kind === 'invalid') return false

      if (command.kind === 'show') {
        const label = isSessionApprovalMode(permissionMode)
          ? APPROVAL_MODE_PRESENTATION[permissionMode].label
          : undefined
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration -- Slash commands apply silently over the relay, so there is no anchored surface for this feedback.
        addToast({
          type: 'info',
          message: composerToastMessage(label ? `Tool permissions are set to “${label}”.` : UNKNOWN_MODE),
        })
        return true
      }

      if (!onApprovalModeChange) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration -- Slash commands apply silently over the relay, so there is no anchored surface for this feedback.
        addToast({type: 'error', message: composerToastMessage(LOCKED_ERROR)})
        userErrorMetrics.shown({code: 'access_denied', surface})
        return true
      }

      const {label} = APPROVAL_MODE_PRESENTATION[command.mode]
      try {
        await onApprovalModeChange(command.mode)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration -- Slash commands apply silently over the relay, so there is no anchored surface for this feedback.
        addToast({type: 'success', message: composerToastMessage(`Tool permissions set to “${label}”.`)})
      } catch {
        // The mutation reports the failure through the picker too, but the
        // command was issued from the composer, which is nowhere near it.
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration -- Slash commands apply silently over the relay, so there is no anchored surface for this feedback.
        addToast({type: 'error', message: composerToastMessage(CHANGE_ERROR)})
        userErrorMetrics.shown({code: 'mutation_failed', surface})
      }
      return true
    },
    [addToast, onApprovalModeChange, permissionMode, surface],
  )
}
